import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUsers } from "../../hooks/useUsers";
import MainPageWrapper from "../../../../../../components/MainPageWrapper/MainPageWrapper";
import UserListFilters from "../UserListFilters/UserListFilters";
import { Button } from "@/components/Button/Button";
import ShowIfHasAccess from "@/components/ShowIfHasAccess/ShowIfHasAccess";
import { User } from "../../slices/usersSlice";
import { createUsersColumns } from "../UserColumns/UserColumns";
import DataTable from "@/components/Datatable/Datatable";
import permissionsService from "@/services/permissionsService";
import { SyncLogsModal } from "../SyncLogsModal/SyncLogsModal";
import { useToast } from "@/components/ui/use-toast";
import { triggerEmployeeSync } from "@/api/remoteSync/remoteSyncApi";

const UserList: React.FC = () => {
  const {
    users,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
    filters,
  } = useUsers();

  const navigate = useNavigate();
  const { hasRole } = permissionsService();
  const isSuperAdmin = hasRole("ROLE_SUPER_ADMIN");
  const { toast } = useToast();

  const [selectedUserForSyncLogs, setSelectedUserForSyncLogs] = useState<User | null>(null);
  const [isSyncLogsModalOpen, setIsSyncLogsModalOpen] = useState(false);

  const handleEdit = useCallback(
    (uuid: string) => {
      navigate(`/hub/users/${uuid}/edit`);
    },
    [navigate],
  );

  const handleImpersonateUser = useCallback(
    (employeeUuid: string) => {
      localStorage.removeItem("selectedCompanyUuid");
      localStorage.setItem("impersonateEmployeeUuid", employeeUuid);
      console.log(`Impersonating employee with UUID: ${employeeUuid}`);
      navigate(0);
    },
    [navigate],
  );

  const handleViewSyncLogs = useCallback((user: User) => {
    setSelectedUserForSyncLogs(user);
    setIsSyncLogsModalOpen(true);
  }, []);

  const handleSyncUser = useCallback(
    async (user: User) => {
      if (!user.employeeId) {
        toast({
          title: "Error",
          description: "Employee ID not found for this user.",
          variant: "destructive",
        });
        return;
      }

      try {
        await triggerEmployeeSync(user.employeeId);
        toast({
          title: "Sync Initiated",
          description: `Sync started for ${user.firstName} ${user.lastName}`,
        });
      } catch (error) {
        toast({
          title: "Sync Failed",
          description: "Failed to initiate sync. Please try again.",
          variant: "destructive",
        });
      }
    },
    [toast]
  );

  // Make sure at least one column has { enableSorting: true }
  const columns = useMemo(
    () => createUsersColumns({ handleEdit, handleImpersonateUser, handleViewSyncLogs, handleSyncUser, isSuperAdmin }),
    [handleEdit, handleImpersonateUser, handleViewSyncLogs, handleSyncUser, isSuperAdmin],
  );

  return (
    <MainPageWrapper
      actions={
        <ShowIfHasAccess
          requiredRoles={["ROLE_OWNER_GM", "ROLE_SUPER_ADMIN"]}
        >
          <Button onClick={() => navigate("/hub/users/create")}>
            Create User
          </Button>
        </ShowIfHasAccess>
      }
      error={error}
      title="Users"
    >
      <UserListFilters
        filters={filters}
        onFilterChange={(filterKey: string, value: string) => {
          handleFilterChange(filterKey, value);
          handlePaginationChange({
            pageIndex: 0,
            pageSize: pagination.pageSize,
          });
        }}
      />

      <div className="relative">
        <DataTable<User>
          columns={columns}
          data={users}
          error={error}
          loading={loading}
          noDataMessage="No users found"
          onPageChange={(newPageIndex, newPageSize) =>
            handlePaginationChange({
              pageIndex: newPageIndex,
              pageSize: newPageSize,
            })
          }
          onSortingChange={handleSortingChange}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          rowKeyExtractor={(item) => item.id}
          sorting={sorting}
          totalCount={totalCount}
        />
      </div>

      <SyncLogsModal
        user={selectedUserForSyncLogs}
        employeeId={selectedUserForSyncLogs?.employeeId ?? null}
        isOpen={isSyncLogsModalOpen}
        onClose={() => setIsSyncLogsModalOpen(false)}
      />
    </MainPageWrapper>
  );
};

export default UserList;
